// /src.wsRecovery/heartbeat.js

// English comments only in this code block

/**
 * Discord-style heartbeat system (client-side)
 * Each socket must implement:
 *  - socket.emit("hb:ping", { ts })
 *  - socket.on("hb:pong", handler)
 *
 * If the socket does not respond within timeoutMs:
 *  -> forced reconnect
 */

const HEARTBEAT_INTERVAL = 10000;   // 10s like Discord's default
const HEARTBEAT_TIMEOUT  = 5000;    // 5s to wait for pong before declaring dead

// Map<socket, { intervalId, timeoutId }>
const socketHeartbeats = new Map();

/**
 * Start a Discord-style heartbeat on a given socket.
 */
export function startHeartbeat(socket) {
  if (!socket) return;
  if (socketHeartbeats.has(socket)) return;

  const data = {
    intervalId: null,
    timeoutId: null
  };

  // English comment: handle server "pong"
  const handlePong = (payload) => {
    clearTimeout(data.timeoutId);
    data.timeoutId = null;
    // console.log("[HB] pong received", payload);
  };

  socket.on("hb:pong", handlePong);

  // English comment: send heartbeat
  function sendPing() {
    // Ignore if disconnected
    if (!socket.connected) return;

    socket.emit("hb:ping", { ts: Date.now() });

    // English comment: expect a pong quickly
    data.timeoutId = setTimeout(() => {
      console.warn("[HB] No pong within timeout → forcing reconnect");

      // Force socket reset (Discord logic)
      try {
        socket.disconnect();
      } catch (_) {}

      try {
        socket.connect();
      } catch (_) {}

    }, HEARTBEAT_TIMEOUT);
  }

  // English comment: start loop
  data.intervalId = setInterval(sendPing, HEARTBEAT_INTERVAL);

  socketHeartbeats.set(socket, data);

  console.log(`[HB] Heartbeat started for namespace "${socket.nsp}"`);
}

/**
 * Stop heartbeat for a given socket
 */
export function stopHeartbeat(socket) {
  const data = socketHeartbeats.get(socket);
  if (!data) return;

  clearInterval(data.intervalId);
  clearTimeout(data.timeoutId);

  socket.off("hb:pong");

  socketHeartbeats.delete(socket);

  console.log(`[HB] Heartbeat stopped for namespace "${socket.nsp}"`);
}

/**
 * Stop all heartbeats
 */
export function stopAllHeartbeats() {
  for (const socket of socketHeartbeats.keys()) {
    stopHeartbeat(socket);
  }
}

/**
 * Restart heartbeat on an already-registered socket
 */
export function restartHeartbeat(socket) {
  stopHeartbeat(socket);
  startHeartbeat(socket);
}
